import type { RawAuthUser } from '@/store/authStore';

const PAYMENT_OPTION_AVAILABLE_KEYS = [
  'paymentOptionAvailableOnWeb',
  'payment_option_available_on_web',
] as const;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

function readOptionalBoolean(
  source: Record<string, unknown>,
  ...keys: readonly string[]
): boolean | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') return true;
      if (normalized === 'false' || normalized === '0') return false;
    }
  }
  return undefined;
}

/**
 * Payments are available only when the institutional billing configuration
 * explicitly enables `paymentOptionAvailableOnWeb`.
 */
export function readPaymentOptionAvailableOnWebFromRawUser(
  rawUser: RawAuthUser | null | undefined,
): boolean {
  const root = asRecord(rawUser);
  if (!root) return false;

  const sources = [root, asRecord(root.user), asRecord(root.data), asRecord(root.result)];
  const available = sources.reduce<boolean | undefined>((resolved, source) => {
    if (resolved !== undefined || !source) return resolved;
    return readOptionalBoolean(source, ...PAYMENT_OPTION_AVAILABLE_KEYS);
  }, undefined);

  return available === true;
}
